import * as React from 'react';
import { observer } from 'mobx-react';
import { ConversationSummary } from 'nomst/Stores/Decision/conversationSummary';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { EntityTypes } from 'typings/enums';
import { ICountryDecisionDispatcher } from 'typings/countryDecision.dispatcher';
import { dataManagerFactory } from 'utils/MobxDataManager/singletons';
import { Conversation } from '@payscale/payscale-conversation-service/lib/';
import './conversationBox.scss';

export interface IConversationBoxProps {
    countryDecisionDispatcher: ICountryDecisionDispatcher;
    conversationSummary: ConversationSummary;
    configuration: Configuration;
    userId: string;
}

export class ConversationBox extends React.Component<IConversationBoxProps> {
    constructor(props: IConversationBoxProps) {
        super(props);

        this.handleOnClose = this.handleOnClose.bind(this);
        this.onMessageRead = this.onMessageRead.bind(this);
        this.onMessageSent = this.onMessageSent.bind(this);
        this.handleModalKeyPress = this.handleModalKeyPress.bind(this);
    }

    render(): React.ReactNode {
        const {
            configuration,
            userId,
            conversationSummary
        } = this.props;

        if (!conversationSummary) {
            return null;
        }

        const conversationDataManager = dataManagerFactory.getConversationDataManager();
        const employeeDataManager = dataManagerFactory.getEmployeeDataManager();

        return [(
            <div key="conversation-wrapper" className="conversation-wrapper">
                <div className="header">
                    <div className="conversation-header-textbox">
                        <div><i className="icon-chat" /></div>
                        <div>
                            <span>Conversation regarding</span>
                            <h4 className="title">
                                {conversationSummary.employeeDisplayName}
                            </h4>
                        </div>
                    </div>
                    <div className="conversation-close">
                        <button
                            className="close pxl-link"
                            onClick={this.handleOnClose}
                        >
                            <i className="icon-cancel" />
                        </button>
                    </div>
                </div>
                <Conversation
                    accessToken={configuration.accessToken}
                    conversationUrl={conversationDataManager.baseUrl}
                    employeeUrl={employeeDataManager.baseUrl}
                    uploadFileSizeLimit={conversationDataManager.fileSizeUploadLimit}
                    fileExtensions={conversationDataManager.fileExtensions}
                    userId={userId}
                    employeeSourceId={conversationSummary.employeeSourceId}
                    masterAccountId={configuration.masterAccountId}
                    entityId={conversationSummary.entityId}
                    entityType={EntityTypes.Employee}
                    unreadMessageCount={conversationSummary.unreadMessageCount}
                    disabled={false}
                    hidePeopleInChat={false}
                    onMessageSent={this.onMessageSent}
                    onMessagesRead={this.onMessageRead}
                />
            </div>
        ), (
            <div
                key="conversation-modal"
                className="pxl-modal conversation-modal"
                onClick={this.handleOnClose}
                onKeyPress={this.handleModalKeyPress}
                role="button"
                tabIndex={0}
            />
        )];
    }

    private handleModalKeyPress(e: React.KeyboardEvent<HTMLDivElement>): void {
        if (e.key === 'Enter') {
            this.handleOnClose();
        }
    }

    private handleOnClose(): void {
        const { countryDecisionDispatcher } = this.props;
        countryDecisionDispatcher.dispatchDeselectConversation();
    }

    private onMessageRead(): void {
        const { conversationSummary } = this.props;
        conversationSummary.resetUnreadMessageCount();
    }

    private async onMessageSent(): Promise<void> {
        const { conversationSummary } = this.props;
        conversationSummary.setHasMessages(true);
        await conversationSummary.sendConversationItemEvent();
    }
}

export default observer(ConversationBox);